import { useNavigate } from 'react-router-dom';
import {
  Button,
  Divider,
  EmptyState,
  IconBan,
  IconBell,
  IconBug,
  IconCalendar,
  IconChat,
  IconClock,
  IconEdit,
  IconHandRaised,
  IconMail,
  IconPhoto,
  IconPin,
  IconRefresh,
  IconSparkles,
  IconUsers,
  Modal,
  Skeleton,
} from '@bala/ui';
import type { ComponentType } from 'react';
import { formatRelative } from '@bala/core';
import type { Notification, NotificationType } from '@bala/core';
import { api, useQuery } from '../api';

/**
 * Icône par type de notification, pour lire la liste d'un coup d'œil.
 *
 * Vectorielles et non emoji : elles suivent la couleur du thème, gardent le
 * même dessin d'un système à l'autre, et se laissent aligner sur la grille de
 * 24 comme le reste de l'interface.
 */
const ICONS: Record<NotificationType, ComponentType<{ size?: number }>> = {
  message_published: IconMail,
  activity_created: IconCalendar,
  activity_updated: IconEdit,
  activity_cancelled: IconBan,
  photos_published: IconPhoto,
  rsvp_reminder: IconClock,
  rsvp_received: IconHandRaised,
  task_assigned: IconPin,
  task_due: IconClock,
  task_status_changed: IconRefresh,
  task_comment: IconChat,
  invitation: IconSparkles,
  mention: IconUsers,
  dev_ticket: IconBug,
  camp_journal_published: IconPhoto,
  camp_reminder: IconCalendar,
};

export function NotificationPanel({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const { data, loading, reload } = useQuery<{ items: Notification[]; unread: number }>(
    open ? '/notifications' : null,
    ['notification'],
  );

  const items = data?.items ?? [];
  const unread = data?.unread ?? 0;

  const markAllRead = async () => {
    await api.post('/notifications/read', {}, { queueOffline: false }).catch(() => undefined);
    reload();
  };

  const openNotification = async (notification: Notification) => {
    if (!notification.readAt) {
      await api.post('/notifications/read', { ids: [notification.id] }, { queueOffline: false }).catch(() => undefined);
    }
    if (notification.link) {
      onClose();
      navigate(notification.link);
    } else {
      reload();
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Notifications"
      subtitle={unread > 0 ? `${unread} non lue(s)` : 'Tout est lu'}
      width={480}
      footer={
        unread > 0 ? (
          <Button variant="secondary" onClick={markAllRead}>
            Tout marquer comme lu
          </Button>
        ) : null
      }
    >
      {loading && items.length === 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s-3)' }}>
          {[0, 1, 2].map((index) => (
            <Skeleton key={index} height={52} radius="var(--r-md)" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          compact
          icon={<IconBell size={28} />}
          title="Aucune notification"
          description="Les tâches qui vous sont assignées, les réponses des parents et les rappels d'échéance apparaîtront ici."
        />
      ) : (
        <ul style={{ display: 'flex', flexDirection: 'column' }}>
          {items.map((notification, index) => (
            <li key={notification.id}>
              {index > 0 && <Divider spacing="0" />}
              <button
                type="button"
                onClick={() => void openNotification(notification)}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 'var(--s-3)',
                  width: '100%',
                  padding: 'var(--s-3) var(--s-2)',
                  textAlign: 'left',
                  borderRadius: 'var(--r-md)',
                  // Un fond légèrement teinté distingue le non-lu sans pastille
                  // supplémentaire.
                  background: notification.readAt ? 'transparent' : 'var(--accent-soft)',
                  transition: 'background var(--d-fast)',
                }}
              >
                <span
                  aria-hidden
                  style={{ display: 'flex', flexShrink: 0, color: 'var(--text-secondary)', marginTop: 1 }}
                >
                  {(() => {
                    const Glyphe = ICONS[notification.type] ?? IconBell;
                    return <Glyphe size={18} />;
                  })()}
                </span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span
                    style={{
                      display: 'block',
                      font: 'var(--t-subhead)',
                      fontWeight: notification.readAt ? 500 : 600,
                    }}
                  >
                    {notification.title}
                  </span>
                  {notification.body && (
                    <span
                      className="bala-clamp-2"
                      style={{ display: 'block', font: 'var(--t-footnote)', color: 'var(--text-secondary)', marginTop: 2 }}
                    >
                      {notification.body}
                    </span>
                  )}
                  <span style={{ display: 'block', font: 'var(--t-caption)', color: 'var(--text-tertiary)', marginTop: 3 }}>
                    {formatRelative(notification.createdAt)}
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
}
